/**
 * useHomeSurfaceCoordinator.ts
 *
 * Central arbitration layer for all transient UI surfaces on the Home viewport.
 *
 * Surface types:
 *   - modal    : blocks everything beneath it (StarterModal)
 *   - floating : fixed overlay balloon/nudge (celebration, guidance, A2HS)
 *   - inline   : in-page-flow card (home message)
 *
 * Rules:
 *   1. Only one modal surface at a time.
 *   2. Only one floating surface at a time.
 *   3. An active modal suppresses all floating and inline surfaces.
 *   4. An active floating surface suppresses inline surfaces.
 *   5. Inline surfaces show only when no modal or floating surface is active.
 *   6. EmailVerificationBanner occupies the top of the viewport in page flow.
 *      A2HS is a fixed top overlay and must be suppressed whenever the banner
 *      is visible — they share the same top region and must never compete.
 *
 * Priority order (highest first):
 *   1. StarterModal        (modal)
 *   2. Celebration balloon (floating)
 *   3. Guidance balloon    (floating)
 *   4. A2HS nudge          (floating) — suppressed when email banner is showing
 *   5. Home message card   (inline)
 *
 * Target: src/hooks/useHomeSurfaceCoordinator.ts
 */

import type { CelebrationResult } from '../services/celebration.service';
import type { GuidanceResult } from '../services/guidance.service';
import type { ResolvedMessage } from '../services/messaging/messaging.types';

export type ActiveModal = 'starter' | null;
export type ActiveFloating = 'celebration' | 'guidance' | 'a2hs' | null;
export type ActiveInline = 'homeMessage' | null;

export interface HomeSurfaceCoordinatorInput {
  // Modal
  starterModalOpen: boolean;
  // Floating
  celebration: CelebrationResult | null;
  guidance: GuidanceResult | null;
  // A2HS is suppressed when the email verification banner is visible —
  // both occupy the top of the viewport and must never compete.
  a2hsNudgeOpen: boolean;
  emailBannerVisible: boolean;
  // Inline
  homeMessage: ResolvedMessage | null;
}

export interface HomeSurfaceCoordinatorOutput {
  activeModal: ActiveModal;
  activeFloating: ActiveFloating;
  activeInline: ActiveInline;
}

/**
 * Pure function — no hooks, no side effects.
 * Call this inside the component with the current eligibility values.
 */
export function resolveHomeSurfaces(
  input: HomeSurfaceCoordinatorInput,
): HomeSurfaceCoordinatorOutput {
  const {
    starterModalOpen,
    celebration,
    guidance,
    a2hsNudgeOpen,
    emailBannerVisible,
    homeMessage,
  } = input;

  // ── Modal slot ────────────────────────────────────────────────────────────
  const activeModal: ActiveModal = starterModalOpen ? 'starter' : null;

  // Modal active — suppress everything else
  if (activeModal) {
    return { activeModal, activeFloating: null, activeInline: null };
  }

  // ── Floating slot — first eligible wins ──────────────────────────────────
  let activeFloating: ActiveFloating = null;
  if (celebration) {
    activeFloating = 'celebration';
  } else if (guidance) {
    activeFloating = 'guidance';
  } else if (a2hsNudgeOpen && !emailBannerVisible) {
    // A2HS suppressed when email banner is visible — they share the top region
    activeFloating = 'a2hs';
  }

  // Floating active — suppress inline
  if (activeFloating) {
    return { activeModal: null, activeFloating, activeInline: null };
  }

  // ── Inline slot — only when no modal or floating surface is active ────────
  const activeInline: ActiveInline = homeMessage ? 'homeMessage' : null;

  return { activeModal: null, activeFloating: null, activeInline };
}
